import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import range from 'lodash/range';
import set from 'lodash/set';
import { Alert, Collapse, FormGroup, Label, Input } from 'reactstrap';

import commonHelper from '@das/canvas-common/src/helper';

import canvasWidgetMap, { WIDGET_NAMES } from 'app/CanvasWidgets/index';
import ContentContext from 'app/ContentContext/components/ContentContext';
import DragAndDropList from 'app/DragAndDropList/container';
import History from 'app/History/components/History';
import ImageSize from 'app/ImageSize/components/ImageSize';
import sharedStyles from 'app/shared.css';
import { ARRAY_SELECTION_WIDGETS, DYNAMIC_COMPONENT_ROUTE } from 'lib/const';
import { contractUtils, validateContract } from 'lib/contract';
import { removeHTML, replaceIndexValues } from 'lib/string';
import { SecondaryButton, SectionHeader } from 'ui/Components';
import { renderArrowStyle } from 'ui/helpers';
import { makeId } from 'utils/selectors';

import NameAndTags from './NameAndTags';

import { ARRAY_TYPE, FIELD_TYPE } from '../const';

const IS_COMPASS_COMPONENT_FIELD = 'IsCompassComponent';
const IS_DO_NOT_ARCHIVE_FIELD = 'IsDoNotArchive';
const IS_STATIC_CONTENT_FIELD = 'IsStaticContent';

/**
 * render the editor form for a nui component based on its contract
 */
export default class GenericForm extends Component {
  static propTypes = {
    boundOpenComponentForEditing: propTypes.func.isRequired,
    onNameChange: propTypes.func.isRequired,
    componentDetails: propTypes.object.isRequired,
    contract: propTypes.object.isRequired,
    editorPane: propTypes.object,
    failedValidations: propTypes.shape(),
    getDynamicValues: propTypes.func.isRequired,
    isFavorite: propTypes.bool,
    isFetchingComponent: propTypes.bool,
    isNewComponent: propTypes.bool,
    isReadonly: propTypes.bool,
    isPreview: propTypes.bool,
    onModifyComponent: propTypes.func.isRequired,
    notifyFailure: propTypes.func.isRequired,
    toggleFavoriteContent: propTypes.func,
    audienceTags: propTypes.array.isRequired
  };

  static defaultProps = {
    editorPane: {},
    failedValidations: {},
    isFavorite: false,
    isFetchingComponent: false,
    isNewComponent: false,
    isReadonly: false,
    isPreview: false,
    toggleFavoriteContent: undefined
  };

  constructor(props) {
    super(props);

    this.state = {
      collapseState: contractUtils.makeCollapseState(props.contract, props.componentDetails),
      expanded: false,
      showDisplayOptions: false,
      showImageSizeSection: false,
      showHistory: false,
      formReadonly: props.isPreview ? false : props.isReadonly
    };

    this.handleCollapseExpandAll = this.handleCollapseExpandAll.bind(this);
    this.handleCompassComponentToggle = this.handleCompassComponentToggle.bind(this);
    this.handleStaticContentToggle = this.handleStaticContentToggle.bind(this);
    this.handleDisplayOptionsClick = this.handleDisplayOptionsClick.bind(this);
    this.handleHistoryClick = this.handleHistoryClick.bind(this);
    this.handleWeightSectionClick = this.handleWeightSectionClick.bind(this);
  }

  componentDidCatch(error, { componentStack }) {
    this.setState({ err: [error.toString(), componentStack].join('\n') });
  }

  /**
   * collapse or expand all sections except for history
   */
  handleCollapseExpandAll() {
    const { contract, componentDetails } = this.props;
    this.setState(ps => ({
      collapseState: contractUtils.makeCollapseState(
        contract,
        componentDetails,
        0,
        !ps.expanded
      ),
      expanded: !ps.expanded,
      showDisplayOptions: !ps.showDisplayOptions
    }));
  }

  /**
   * toggle display options section
   */
  handleDisplayOptionsClick() {
    this.setState(ps => ({ showDisplayOptions: !ps.showDisplayOptions }));
  }

  /**
   * toggle display options section
   */
  handleHistoryClick() {
    this.setState(ps => ({ showHistory: !ps.showHistory }));
  }

  /**
   * Toggle weight section click
   */
  handleWeightSectionClick() {
    this.setState(ps => ({ showImageSizeSection: !ps.showImageSizeSection }));
  }

  /**
   * handle compass component toggle action
   * @param {object} e - browser event
   * @returns {undefined}
   */
  handleCompassComponentToggle(e) {
    const { onModifyComponent } = this.props;

    onModifyComponent(e.target.checked, IS_COMPASS_COMPONENT_FIELD);
    onModifyComponent('', DYNAMIC_COMPONENT_ROUTE);
  }

  /**
   * handle static content checkbox toggle
   *
   * @param {object} e - input change event
   */
  handleStaticContentToggle(e) {
    const { onModifyComponent } = this.props;

    onModifyComponent(e.target.checked, IS_STATIC_CONTENT_FIELD);
  }

  /**
   * update collapse state by setting a value at a location
   * @param {any} value - value to be set
   * @param {string} location - object path to location of state to be updated
   */
  updateCollapseState(value, location) {
    this.setState(ps => ({
      collapseState: set(cloneDeep(ps.collapseState), location, value)
    }));
  }

  /**
   * a helper function to render a canvas field based on the contract
   * @param {object} child - a canvas field object
   * @param {object} obj - recursion info object
   * @param {string} obj.dependsOnFieldLocation - the location of the field that this depends on
   * @param {string} obj.location - the current section field location
   * @returns {React.Component} rendered Field Input
   */
  renderFieldSection(child, { dependsOnFieldLocation = '', location = '' } = {}) {
    const {
      boundOpenComponentForEditing,
      componentDetails,
      contract,
      editorPane,
      failedValidations,
      getDynamicValues,
      notifyFailure,
      onModifyComponent
    } = this.props;
    const { formReadonly } = this.state;
    const failedValidation = failedValidations[location];

    // check for any dependent fields
    if (child.dependsOnField) {
      const selectedDependsOnFieldValue = get(componentDetails, dependsOnFieldLocation, '');
      if (
        (Array.isArray(selectedDependsOnFieldValue) &&
          !selectedDependsOnFieldValue.includes(child.dependsOnFieldIncludesValue)) ||
        (!Array.isArray(selectedDependsOnFieldValue) &&
          selectedDependsOnFieldValue !== child.dependsOnFieldValue)
      ) {
        return null;
      }
    }

    // check for any constant fields
    if (child.constant) {
      return null;
    }

    // props used by all widgets
    const baseProps = {
      isReadonly: formReadonly,
      fieldContract: child,
      onChange: val =>
        onModifyComponent(typeof val === 'string' ? val.trim() : val, location, child),
      selectedValue: ARRAY_SELECTION_WIDGETS.includes(child.canvasWidget)
        ? get(componentDetails, location, child.defaultValues || [])
        : get(componentDetails, location, child.defaultValue || ''),
      updateLocation: location,
      validationMessage: get(failedValidation, 'message')
    };

    let formProps;
    switch (child.canvasWidget) {
      case WIDGET_NAMES.AMPConnector:
        formProps = {
          ...baseProps,
          notifyFailure
        };
        break;
      case WIDGET_NAMES.AttachChildInput: {
        const items = get(componentDetails, location, []);
        const { minLength = 0 } = child;

        formProps = {
          ...baseProps,
          boundOpenComponentForEditing,
          items,
          beforeRemove: () => {
            if (items.length <= minLength) {
              notifyFailure(
                `${contract.name} needs at least ${minLength} children to render.`
              );
              return false;
            }
            return true;
          },
          modifyComponent: onModifyComponent
        };
        break;
      }
      case WIDGET_NAMES.ButtonList:
      case WIDGET_NAMES.Dropdown:
      case WIDGET_NAMES.RadioButton: {
        // props to override
        let { allowedValues = [] } = child;

        // handle dynamic allowedValues
        if (!Array.isArray(allowedValues)) {
          // check for existing values
          const { allowedValues: ignore, ...rest } = child;
          const { apiUrl } = allowedValues;
          allowedValues = get(editorPane, makeId({ ...rest, apiUrl }));

          // request values and temporarily use dummy values
          if (!allowedValues) {
            getDynamicValues(child);
            allowedValues = ['loading...'];
          }

          // prepend an empty entry for dropdowns
          allowedValues =
            child.canvasWidget === WIDGET_NAMES.Dropdown
              ? [''].concat(allowedValues)
              : allowedValues;
        }

        // ensure allowedValues is array of objects form (i.e. { displayName, value })
        allowedValues = allowedValues.map(value =>
          typeof value === 'string' ? { value, displayName: value } : value
        );

        // override props
        formProps = {
          ...baseProps,
          isReadonly: formReadonly,
          fieldContract: {
            ...baseProps.fieldContract,

            // only pass displayName to widget
            allowedValues: allowedValues.map(({ displayName }) => displayName)
          },

          // index-based onChange and selectedValue
          onChange: ind =>
            onModifyComponent(get(allowedValues, [ind, 'value']), location, child),
          selectedValue: allowedValues.findIndex(
            ({ value }) =>
              isEqual(baseProps.selectedValue, value) ||
              (value &&
                child.valueComparisonProp &&
                baseProps.selectedValue[child.valueComparisonProp] ===
                  value[child.valueComparisonProp])
          )
        };
        break;
      }
      default:
        formProps = baseProps;
    }

    const FormInput = canvasWidgetMap[child.canvasWidget];
    return (
      <div key={makeId({ location, name: child.name })} className="mx-4 my-2">
        <FormInput {...formProps} />
      </div>
    );
  }

  /**
   * when called this will return an array section or
   * re-orderable array section based on the contract
   * @param {object} child - a canvas section child (array) object
   * @param {object} obj - recursion info object
   * @param {string} obj.location - prop that gives the current section field location
   * @param {string} obj.nextStateLocation - prop location for the next state
   * @param {Array} obj.replaceIndicies - array of indicies used in propLocation replacement
   * @param {number} obj.level - level (depth) of recursion
   * @returns {React.Component} array section component
   */
  renderArraySection(
    child,
    { location = '', nextStateLocation = '', replaceIndicies = [], level = 0 }
  ) {
    const { componentDetails, onModifyComponent } = this.props;
    const { collapseState, formReadonly } = this.state;
    const arrayLength = get(componentDetails, location, []).length;

    // build the section header
    const sectionHeader = range(arrayLength).map(index => {
      const headerNextStateLocation = `${nextStateLocation}.items[${index}]`;
      const headerChildStateLocation = `${headerNextStateLocation}.open`;
      const headerOpen = get(collapseState, headerChildStateLocation, false);

      // used to change the section name based on the user input.
      // 0 is to pick inner list top element eg: copyset label from live text headline list.
      const sectionLabelLocation = replaceIndexValues(
        child.labelPropLocation || '',
        ...replaceIndicies,
        index,
        0
      );

      // get section name from based on the user input from a specific field
      const sectionName = get(componentDetails, sectionLabelLocation);
      const name =
        removeHTML(sectionName) || `${child.displayName || child.name} ${index + 1}`;

      return (
        <SectionHeader
          key={name}
          isDelete
          isReadonly={formReadonly}
          level={level + 1}
          sectionToggle={() => {
            // remove array element from props
            const newArray = get(componentDetails, location, []).filter(
              (item, itemIndex) => itemIndex !== index
            );
            onModifyComponent(newArray, location);

            // remove collapse state for array element
            const itemsLocation = `${nextStateLocation}.items`;
            const newItems = get(collapseState, itemsLocation, []).filter(
              (item, itemIndex) => itemIndex !== index
            );
            this.updateCollapseState(newItems, itemsLocation);
          }}
          onClick={() => this.updateCollapseState(!headerOpen, headerChildStateLocation)}
          data-test-id={makeId({ location: headerChildStateLocation })}
        >
          {name}
          {renderArrowStyle(headerOpen)}
        </SectionHeader>
      );
    });

    // build the section body
    const sectionBody = range(arrayLength).map(index => {
      const bodyNextStateLocation = `${nextStateLocation}.items[${index}]`;
      const bodyChildStateLocation = `${bodyNextStateLocation}.open`;
      const itemOpen = get(collapseState, bodyChildStateLocation, false);

      return (
        <Collapse isOpen={itemOpen}>
          {this.renderSections(child, {
            level: level + 2,
            replaceIndicies: replaceIndicies.concat(index),
            stateLocation: bodyNextStateLocation
          })}
        </Collapse>
      );
    });

    // array sections can be re-ordered
    if (child.reorderable) {
      /**
       * changes the ordering of items based on the index values provided
       * @param {number} dragIndex - index value of the dragged section
       * @param {number} hoverIndex - index value of the section hovered over
       */
      const changeItemsOrder = (dragIndex, hoverIndex) => {
        const dragItems = [...get(componentDetails, location, [])];
        const dragCard = dragItems[dragIndex];
        dragItems.splice(dragIndex, 1);
        dragItems.splice(hoverIndex, 0, dragCard);
        onModifyComponent(dragItems, location);
      };
      return (
        <DragAndDropList body={sectionBody} changeItemsOrder={changeItemsOrder}>
          {sectionHeader}
        </DragAndDropList>
      );
    }

    // return the array section by combining section-header and section-body
    return range(arrayLength).map(index => (
      <Fragment key={index}>
        {sectionHeader[index]}
        {sectionBody[index]}
      </Fragment>
    ));
  }

  /**
   * recursively render inputs for a canvas section object
   * @param {object} section - a canvas section (array, object, or field) object
   * @param {object} obj - recursion info object
   * @param {Array} obj.replaceIndicies - array of indicies used in propLocation replacement
   * @param {number} obj.level - level used for headers (based on recursion depth)
   * @param {string} obj.stateLocation - path within collapse state for current section
   * @returns {React.Component} form inputs for the section
   */
  renderSections(section, { replaceIndicies = [], level = 0, stateLocation = '' } = {}) {
    const { componentDetails, onModifyComponent } = this.props;
    const { collapseState, expanded, formReadonly } = this.state;

    return section.children.map(child => {
      // build path for current props
      const location = replaceIndexValues(child.propLocation, ...replaceIndicies);
      const dependsOnFieldLocation = replaceIndexValues(
        child.dependsOnField,
        ...replaceIndicies
      );

      // render a form input for fields
      if (child.type === FIELD_TYPE) {
        return this.renderFieldSection(child, {
          location,
          dependsOnFieldLocation
        });
      }

      // create collapse state for sections
      const nextStateLocation = !stateLocation ? child.name : `${stateLocation}.${child.name}`;
      const childStateLocation = `${nextStateLocation}.open`;
      const isOpen = get(collapseState, childStateLocation, true);

      // setup header
      const headerProps = {
        isReadonly: formReadonly,
        level,
        'data-test-id': makeId({ location: childStateLocation }),
        onClick: () => this.updateCollapseState(!isOpen, childStateLocation)
      };

      // array section
      const isArray = child.type === ARRAY_TYPE;
      let arraySection = null;

      // add toggle for array sections
      if (isArray) {
        headerProps.sectionToggle = () => {
          // add default props for new array element
          const defaults = contractUtils.getDefaultsFromSection(child, true);

          const array = get(componentDetails, location, []);
          onModifyComponent(array.concat(defaults), location);

          // add collapse state for new array element based on current `expanded` state
          const { items } = contractUtils.makeStateFromSection(
            child,
            componentDetails,
            replaceIndicies[replaceIndexValues.length - 1],
            expanded
          );
          const itemsLocation = `${nextStateLocation}.items`;
          const newItems = get(collapseState, itemsLocation, []).concat(items);
          this.updateCollapseState(newItems, itemsLocation);
        };

        arraySection = this.renderArraySection(child, {
          location,
          nextStateLocation,
          replaceIndicies,
          level
        });
      }

      return (
        <Fragment key={child.name}>
          <SectionHeader {...headerProps}>
            {child.name}
            {renderArrowStyle(isOpen)}
          </SectionHeader>
          <Collapse isOpen={isOpen}>
            {isArray
              ? arraySection
              : this.renderSections(child, {
                  replaceIndicies,
                  level: level + 2,
                  stateLocation: nextStateLocation
                })}
          </Collapse>
        </Fragment>
      );
    });
  }

  render() {
    const {
      onNameChange,
      componentDetails,
      contract,
      failedValidations,
      isFavorite,
      isFetchingComponent,
      onModifyComponent,
      isNewComponent,
      toggleFavoriteContent,
      audienceTags
    } = this.props;
    const {
      expanded,
      err,
      showDisplayOptions,
      showHistory,
      showImageSizeSection,
      formReadonly
    } = this.state;

    // render error message on errors
    if (err) {
      return (
        <Alert color="danger">
          <pre>{err}</pre>
        </Alert>
      );
    }

    // do not attempt to render invalid contracts
    const contractErrors = validateContract(contract);
    if (contractErrors) {
      return (
        <Alert color="danger">
          <pre>{contractErrors}</pre>
        </Alert>
      );
    }
    const contentSizes = commonHelper.calculateTotalContentSize(componentDetails);

    return (
      <div>
        <SectionHeader>Name & Tagging</SectionHeader>
        <NameAndTags
          key={componentDetails.Id}
          component={componentDetails}
          onNameChange={onNameChange}
          nameValidationMessage={get(failedValidations, 'ComponentName.message')}
          routeValidationMessage={get(failedValidations, 'DynamicComponent.message')}
          isCompassComponent={componentDetails[IS_COMPASS_COMPONENT_FIELD]}
          isDynamic={componentDetails.IsDynamic}
          isLive={componentDetails.LiveNow}
          isFavorite={isFavorite}
          isFetchingComponent={isFetchingComponent}
          isReadonly={formReadonly}
          name={componentDetails.ComponentName}
          route={componentDetails.ComponentRoute}
          onFavoriteClick={toggleFavoriteContent}
          modify={onModifyComponent}
          showFavoriteIcon={!isNewComponent}
          startDate={componentDetails.StartDate}
          audienceTags={audienceTags}
          tags={componentDetails.Tags}
        />
        <div className={`p-4 ${sharedStyles.borderTopDashedLight}`}>
          <SecondaryButton
            onClick={this.handleCollapseExpandAll}
            data-test-id={makeId({
              component: this.constructor.name,
              name: 'collapse/expand all'
            })}
          >
            {`${expanded ? '- Collapse' : '+ Expand'} All`}
          </SecondaryButton>
          <FormGroup check inline className="ml-4">
            <Label className="font-weight-bold">
              <Input
                disabled={formReadonly}
                type="checkbox"
                checked={componentDetails[IS_DO_NOT_ARCHIVE_FIELD]}
                data-test-id="component-doNotArchiveCheckbox"
                onChange={e => onModifyComponent(e.target.checked, IS_DO_NOT_ARCHIVE_FIELD)}
              />
              Do Not Archive
            </Label>
          </FormGroup>
        </div>
        {componentDetails.IsDynamic && (
          <div className="pl-4 pb-4">
            <FormGroup check inline>
              <Label className="font-weight-bold">
                <Input
                  checked={componentDetails[IS_COMPASS_COMPONENT_FIELD]}
                  data-test-id="component-isCompassComponent"
                  disabled={formReadonly}
                  onChange={this.handleCompassComponentToggle}
                  type="checkbox"
                />
                Compass Component
              </Label>
              <Label className="font-weight-bold ml-4">
                <Input
                  checked={componentDetails[IS_STATIC_CONTENT_FIELD]}
                  data-test-id="component-isStatic"
                  disabled={formReadonly}
                  onChange={this.handleStaticContentToggle}
                  type="checkbox"
                />
                Static Content
              </Label>
            </FormGroup>
          </div>
        )}
        {!contract.customDisplayOptions && (
          <Fragment>
            <SectionHeader
              onClick={this.handleDisplayOptionsClick}
              data-test-id={makeId({
                component: this.constructor.name,
                section: 'display options'
              })}
            >
              Display Options
              {renderArrowStyle(showDisplayOptions)}
            </SectionHeader>
            <Collapse isOpen={showDisplayOptions}>
              <ContentContext
                componentDetails={componentDetails}
                isReadonly={formReadonly}
                modifyComponent={onModifyComponent}
              />
            </Collapse>
          </Fragment>
        )}
        {this.renderSections(contract)}
        <SectionHeader onClick={this.handleWeightSectionClick}>
          Content Weight
          {renderArrowStyle(showImageSizeSection)}
        </SectionHeader>
        <Collapse isOpen={showImageSizeSection}>
          {showImageSizeSection && <ImageSize contentSizes={contentSizes} />}
        </Collapse>
        <SectionHeader
          onClick={this.handleHistoryClick}
          data-test-id={makeId({
            component: this.constructor.name,
            section: 'history'
          })}
        >
          Component History
          {renderArrowStyle(showHistory)}
        </SectionHeader>
        <Collapse isOpen={showHistory}>
          <History history={componentDetails.History} />
        </Collapse>
      </div>
    );
  }
}
